import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Stack, Typography, useMediaQuery } from '@mui/material'
import { Alert, Button } from '@repo/ui'

import { useAmbientSurge } from 'layouts/LayoutAuthAmbient/ambientSurge'
import { ROUTES } from 'routes'
import { signupApi } from 'services/signupApi'
import { storage } from 'utils'
import { endVeraSpaceSession } from 'stores'

import { AuthLogo } from '../components/AuthLogo'
import { authGlassCardSx } from '../components/authGlassCard'
import { AuthPrimaryButton } from '../components/AuthPrimaryButton'
import { SignupSuccessMark, type SignupSuccessMarkPhase } from '../components/SignupSuccessMark'

/**
 * VERA-1095 slice 4 / VRG-289 — where Stripe returns after payment.
 *
 * On the PLATFORM, not apps/app. The founder fills the signup form here, so
 * being handed to the customer certificate app to be told their payment worked
 * dropped them into a different product, on a different origin, with no session
 * and nothing to act on.
 *
 * This calls /signup/verify, which — for the founder path — is where the
 * company is actually provisioned (deferred provisioning, VRG-289). It is
 * the FAST path, no longer the only one: the `payment_intent.succeeded` webhook
 * activates/provisions server-side too, so closing this tab no longer strands
 * a paid signup. That is also why an exhausted retry here is reported as
 * "payment received, activation still settling" rather than as a failure —
 * the money arrived, and the webhook will finish the job.
 *
 * VERA-1095 (Elements): reached with `?payment_intent=` now, not
 * `?session_id=`. Signup confirms a PaymentIntent in-page rather than handing
 * the browser to Stripe Checkout, so the page navigates here itself once
 * `confirmPayment` resolves (and Stripe's own 3DS `return_url` lands here with
 * the same param name, which is why that name and not a bespoke one).
 * `session_id` is still read, because a signup STARTED before this change is
 * only resolvable that way and its return link already exists.
 *
 * ── THIS PASS ─────────────────────────────────────────────────────────────
 *
 * 1. LANDS ON HOME, not `/certificates`. Per VRG-339 the post-auth destination
 *    is `ROUTES.HOME` and `HomePage` decides what the viewer should actually
 *    see (internal staff -> internal console, in-space -> settings, a fresh
 *    founder -> the getting-started checklist). A surface that picks its own
 *    destination re-derives that decision from a role it has barely loaded,
 *    which is exactly what VRG-339 removed. There is no hardcoded destination
 *    on this page any more.
 *
 * 2. ROUTED UNDER `LayoutAuthAmbient` (App.tsx), where it previously sat under
 *    plain `LayoutAuth`. Two things follow. The ambient blob field is now
 *    present on this page at all — it was not before, so arriving here from
 *    signup meant the whole backdrop vanished at the moment of success. And
 *    because the layout survives the navigation, the field does not re-mount:
 *    it is the SAME blobs, which is what lets them converge (see `surge`).
 *
 * 3. THE WAITING STATE IS CENTRED. It used to be a heading, a rule and a line
 *    of small print pinned to the top-left of a 440px outlet — which read as a
 *    page that had failed to finish loading rather than as a deliberate wait.
 *
 * 4. A REAL FAILURE STATE. Distinguishing "still settling" from "the payment
 *    failed" is done by asking Stripe, not by inferring from our own verify
 *    call: `/signup/verify` answers "is this company activated", and a
 *    not-yet-activated signup looks identical whether the charge is settling or
 *    was declined at 3DS. Reporting a declined card as "Payment received" is
 *    the specific defect that motivated this.
 */

const MAX_RETRIES = 5
const RETRY_BASE_MS = 2000 // 2s, doubling: 2 → 4 → 8 → 16 → 32

type PageState = 'loading' | 'success' | 'pending' | 'failed' | 'error'

/**
 * PaymentIntent statuses that mean the money did NOT arrive and will not arrive
 * without the visitor doing something. Anything else — `processing`,
 * `succeeded`, `requires_capture` — is either done or still in flight, and is
 * handled by the existing verify/retry path.
 */
const FAILED_INTENT_STATUSES = new Set(['requires_payment_method', 'canceled'])

/**
 * What to SHOW someone whose payment could not be confirmed.
 *
 * NEVER `err.message`. On an axios rejection that string is the transport
 * status — "Request failed with status code 404" — and this screen was printing
 * it verbatim, directly beneath copy promising the visitor their payment is
 * safe. A raw stack-adjacent string under that sentence reads as though
 * something is broken beyond it, at the exact moment someone has just been
 * charged and is deciding whether to trust us.
 *
 * Removing the Alert entirely was the other option and is worse: it leaves the
 * visitor and support with nothing to go on. So: prefer OUR API's own message
 * when it sent one (those are written for people), otherwise our own sentence —
 * and the technical detail goes to the console for whoever is debugging, not to
 * the person.
 */
const TRANSPORT_NOISE = /request failed|network error|status code|timeout of|ECONN|ERR_/i

const humanErrorMessage = (err: unknown): string => {
  const apiMessage = (err as { response?: { data?: { message?: unknown } } })?.response?.data
    ?.message
  if (typeof apiMessage === 'string' && apiMessage.trim() && !TRANSPORT_NOISE.test(apiMessage)) {
    return apiMessage
  }
  return "We couldn't reach our servers to finish confirming this."
}

/**
 * Heading text that assembles per word instead of appearing whole.
 *
 * NOT a typewriter. A literal left-to-right reveal (especially with a cursor)
 * reads as terminal chrome, and it is the same register as the fill-and-pop
 * this screen just moved away from — it would undo the restraint. A short
 * per-word fade-and-rise reads as the sentence settling into place, which is
 * the same gesture the mark makes.
 *
 * WORDS, not characters. Per-character stagger on a 3-word heading is 13 nodes
 * animating over ~600ms, which reads as jitter at this size; and it fragments
 * the text for anything walking the DOM.
 *
 * ACCESSIBILITY: the split spans are `aria-hidden` and the real string is on
 * the container's `aria-label`, so a screen reader gets one heading rather than
 * a stream of disconnected words. Under reduced motion the words are simply
 * present — no offset, no stagger, nothing to wait for.
 *
 * `key={text}` on the caller's side is what re-runs this when the heading
 * changes from "Confirming your payment" to "You're all set" — without it the
 * spans keep their finished styles and the new words appear instantly.
 */
const AssemblingHeading = ({ text, reduceMotion }: { text: string; reduceMotion: boolean }) => (
  <Typography variant="h1" textAlign="center" aria-label={text}>
    {text.split(' ').map((word, index) => (
      <Box
        key={`${word}-${index}`}
        aria-hidden
        component="span"
        sx={{
          display: 'inline-block',
          // A trailing space inside an inline-block is collapsed, so the gap
          // between words has to be re-added as margin or they run together.
          mr: '0.28em',
          opacity: reduceMotion ? 1 : 0,
          transform: reduceMotion ? 'none' : 'translateY(0.32em)',
          animation: reduceMotion ? 'none' : 'veraWordSettle 620ms cubic-bezier(0.22, 1, 0.36, 1) forwards',
          animationDelay: `${index * 90}ms`,
          '@keyframes veraWordSettle': {
            to: { opacity: 1, transform: 'translateY(0)' },
          },
        }}
      >
        {word}
      </Box>
    ))}
  </Typography>
)

/**
 * Shared centred shell. Matches VerifyEmailPage / PasswordSetup: the backdrop
 * belongs to `LayoutAuthAmbient`, so this is layout only.
 *
 * MODULE SCOPE, NOT INSIDE THE PAGE COMPONENT — and this is load-bearing, not
 * tidiness. It used to be declared in the component body, which makes it a NEW
 * function identity on every render. React compares element types by identity,
 * so a new identity is a DIFFERENT COMPONENT: every state change unmounted this
 * entire subtree and mounted a fresh one.
 *
 * That is fatal to any transition. A CSS transition needs the same element to
 * hold an old value and then be given a new one; a remounted element is created
 * already at its final value, with nothing to interpolate from. So the mark's
 * four beats, the check's draw-on, and the CTA's `grid-template-rows` reveal all
 * silently rendered as instant jumps — the screen "just flashed" no matter what
 * the timings said, because the beats WERE firing and each one was throwing away
 * the DOM it was supposed to be animating.
 *
 * It also meant every keystroke-equivalent re-render rebuilt the whole screen,
 * which is why this was worth finding rather than working around.
 *
 * Never move a component declaration inside another component's body.
 */
const Shell = ({ children, showLogo = true }: { children: React.ReactNode; showLogo?: boolean }) => (
  <Box
    sx={{
      position: 'fixed',
      inset: 0,
      bgcolor: 'transparent',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'auto',
      px: 6,
      py: 10,
    }}
  >
    <Box
      sx={[
        // Phones only: the solid glass sheet every auth page has below `sm`
        // (authGlassCard.ts, 2026-10-08); desktop unchanged. This shell's phone
        // gutter is 24px (px: 6), which the sheet's side inset cancels. FIRST, so
        // `position`/`zIndex` below win.
        (theme) => authGlassCardSx(theme, { desktop: false, phoneGutter: 24 }),
        {
        position: 'relative',
        zIndex: 2,
        width: '420px',
        maxWidth: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '48px',
        },
      ]}
    >
      {/* THE WORDMARK AND THE MARK NEVER APPEAR TOGETHER.
          The success mark is a large animated V; a static VERA wordmark 48px
          above it is the same letterform at a similar weight, so the two
          competed — the page read as having two logos. Whichever brand element
          is present carries the brand, so states that render the mark
          (confirming / arrived / still-settling) suppress this, and the states
          that have no mark at all (failed, error) keep it and are the reason
          it still exists here. */}
      {showLogo && <AuthLogo />}
      {children}
    </Box>
  </Box>
)

export const SignupSuccessPage = () => {
  const navigate = useNavigate()
  const { setSurge } = useAmbientSurge()
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)')
  const [state, setState] = useState<PageState>('loading')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const retryCount = useRef(0)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  /**
   * The minted session token, HELD until "Start connecting" is pressed rather
   * than written to storage the moment it arrives.
   *
   * This is load-bearing and was a real defect when it worked the other way.
   * This page is routed under `UnProtectedRoute`, whose guard is
   * `isAuthenticated` — which is derived from the stored token
   * (`useWorkOSAuth`: `!!user && workosAuthService.isAuthenticated()`). Writing
   * the token here therefore makes this very page unauthenticated-only-no-more:
   * the profile query switches on, `user` populates, the guard flips, and it
   * renders `<Navigate to={ROUTES.HOME} replace />` — evicting the success
   * screen a beat after it appears.
   *
   * Measured: the visitor never saw "You're all set" at all. They were bounced
   * to Home directly, which LOOKS like it works (they do end up in the right
   * place) and is exactly why it would have survived a casual test.
   *
   * A ref rather than state because nothing renders from it; storing it is a
   * side effect of pressing the button.
   */
  const pendingToken = useRef<string | null>(null)

  // `payment_intent` is what both routes into this page carry: the signup
  // page's own navigation after `confirmPayment`, and Stripe's 3DS redirect
  // (which appends `payment_intent` to the `return_url` itself).
  const params = new URLSearchParams(window.location.search)
  const paymentIntentId = params.get('payment_intent')
  // Stripe appends this alongside `payment_intent` on its own redirect. It is
  // what lets this page read the intent's REAL status without an API call and
  // without any secret of ours — see `FAILED_INTENT_STATUSES`.
  const paymentIntentClientSecret = params.get('payment_intent_client_secret')
  // LEGACY — a signup started before the move to Elements.
  const sessionId = params.get('session_id')
  const reference = paymentIntentId ?? sessionId

  /**
   * Ask Stripe whether this intent has definitively failed.
   *
   * Deliberately best-effort and non-fatal: if stripe.js is unavailable, the
   * key is missing, or the client secret was not passed through, we fall back
   * to the existing "still settling" behaviour rather than inventing a failure.
   * Saying "your payment failed" to someone whose payment actually worked is a
   * far worse error than the reverse.
   */
  const hasDefinitivelyFailed = useCallback(async (): Promise<string | null> => {
    if (!paymentIntentClientSecret) return null
    try {
      const { STRIPE_PUBLIC_KEY } = await import('config')
      if (!STRIPE_PUBLIC_KEY) return null
      const { loadStripe } = await import('@stripe/stripe-js')
      const stripe = await loadStripe(STRIPE_PUBLIC_KEY as string)
      if (!stripe) return null
      const { paymentIntent } = await stripe.retrievePaymentIntent(paymentIntentClientSecret)
      if (!paymentIntent || !FAILED_INTENT_STATUSES.has(paymentIntent.status)) return null
      return (
        paymentIntent.last_payment_error?.message ??
        'Your card was not charged. The payment did not go through.'
      )
    } catch {
      return null
    }
  }, [paymentIntentClientSecret])

  const attempt = useCallback(async () => {
    if (!reference) return
    try {
      const result = paymentIntentId
        ? await signupApi.verifyPayment(paymentIntentId)
        : await signupApi.verifyCheckout(sessionId as string)
      if (result.activated) {
        // The token is HELD, not stored — see `pendingToken`.
        if (result.token) pendingToken.current = result.token
        setState('success')
        return
      }
      // Not activated. Before treating that as "still settling", check whether
      // the intent actually failed — those two look identical from here.
      const failureMessage = await hasDefinitivelyFailed()
      if (failureMessage) {
        setErrorMessage(failureMessage)
        setState('failed')
        return
      }
      // Genuinely not settled yet. Back off and try again; the webhook is also
      // racing to do this, so giving up here is not the same as failing.
      setState('pending')
      if (retryCount.current < MAX_RETRIES) {
        const delay = RETRY_BASE_MS * Math.pow(2, retryCount.current)
        retryCount.current += 1
        timerRef.current = setTimeout(attempt, delay)
      }
    } catch (err) {
      // The technical detail goes HERE, not onto the screen. See
      // `humanErrorMessage` for why the raw message must never be rendered.
      console.error('[signup] verify failed', { reference, err })
      setState('error')
      setErrorMessage(humanErrorMessage(err))
    }
  }, [reference, paymentIntentId, sessionId, hasDefinitivelyFailed])

  useEffect(() => {
    if (!reference) {
      setState('error')
      setErrorMessage(
        'This link is missing its payment reference. If you completed payment, contact support. Your payment is safe.',
      )
      return
    }
    attempt()
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [reference, attempt])

  /**
   * Tell the ambient field to converge on success, and release it on unmount so
   * a later auth screen (sign-in, after signing out) is not left with a
   * compressed, fast-running background.
   *
   * The backdrop owns what "1" looks like — see `SURGE_COMPRESSION` /
   * `SURGE_RATE_GAIN` in SignInBackground. This page only states the intent.
   */
  useEffect(() => {
    if (state !== 'success') return undefined
    setSurge(1)
    return () => setSurge(0)
  }, [state, setSurge])

  /**
   * The arrival choreography — four beats rather than one simultaneous flip.
   *
   * Every property used to change at once the instant `state` became
   * `success`: the V swapped for the check, the ambient field started
   * compressing, and the CTA appeared, all on overlapping ~420ms transitions.
   * That reads as a state CHANGE, not as an arrival, because nothing leads to
   * anything else — and it is what made the screen look like it snapped.
   *
   * The beats and their spacing (see `SignupSuccessMark` for what each one
   * renders):
   *
   *   0ms     closing  the iris sweeps closed around the V over 1100ms, while
   *                    the ambient field compresses and swirls on its own
   *                    1600ms curve — the two are still moving together
   *   950ms   check    starts 150ms BEFORE the iris finishes, deliberately.
   *                    Waiting for it to complete left a dead beat and read as
   *                    two separate events; overlapping slightly makes the
   *                    check feel caused by the iris rather than queued after
   *                    it. Draws over 820ms, ending ~1770ms.
   *   1800ms  cta      just after the check lands.
   *
   * SLOWER THAN THE VERSION THIS REPLACED (1400ms), and that is the point. The
   * previous timing served a fill-and-pop, which is a fast gesture; a hairline
   * iris and a single drawn stroke are slow gestures, and rushing them is what
   * would make them read as cheap.
   *
   * REDUCED MOTION JUMPS STRAIGHT TO `done` rather than running the beats
   * instantly. Someone who has asked for less motion should not be made to
   * wait 1.8s for their button; and the mark's `done` phase already renders a
   * closed iris and a complete check with no transition, so the screen is
   * correct and complete in that one frame.
   */
  const [phase, setPhase] = useState<SignupSuccessMarkPhase>('waiting')

  useEffect(() => {
    if (state !== 'success') {
      setPhase('waiting')
      return undefined
    }
    if (prefersReducedMotion) {
      setPhase('done')
      return undefined
    }
    setPhase('closing')
    const timers = [
      setTimeout(() => setPhase('check'), 950),
      setTimeout(() => setPhase('done'), 1800),
    ]
    return () => timers.forEach(clearTimeout)
  }, [state, prefersReducedMotion])

  const handleRetry = () => {
    retryCount.current = 0
    setState('loading')
    attempt()
  }

  /**
   * Into the app. A FULL navigation, not client-side routing — the token was
   * just written to storage and the app has to bootstrap fresh with it rather
   * than this public onboarding tree somehow becoming the authenticated one in
   * place.
   *
   * `ROUTES.HOME`, and nothing more specific: HomePage owns the decision about
   * where this person actually belongs (VRG-339).
   */
  const goHome = () => {
    // Store the held token HERE — the last thing before leaving, so the guard
    // that would evict this page only ever sees it once we are already going.
    // See `pendingToken`.
    if (pendingToken.current) {
      // VRG-661: a new session never resumes a company space left in this browser.
      endVeraSpaceSession()
      storage.setItem('accessToken', pendingToken.current)
      storage.removeItem('refreshToken')
    }
    window.location.href = ROUTES.HOME
  }

  /**
   * LOADING AND SUCCESS ARE ONE BRANCH, deliberately — this is the fix for
   * "the page flashes to You're all set instead of animating height to fit".
   *
   * They used to be two separate `return`s. React therefore UNMOUNTED the
   * waiting tree and MOUNTED a different one, so no transition was possible in
   * principle: the mark, the heading and the body were different element
   * instances, and the CTA arrived in the same frame as everything else. No
   * amount of CSS on either tree can animate across a remount.
   *
   * Sharing one tree means the mark instance PERSISTS across verification —
   * which is also what lets its phase transitions run at all — and the only
   * genuine height change left is the CTA arriving. That is animated below
   * with `grid-template-rows: 0fr -> 1fr`, which needs no measurement, no
   * ResizeObserver and no rAF, and so degrades to an instant (but correct)
   * layout anywhere those are missing.
   *
   * The copy pair is length-matched on purpose: at this 420px column both
   * headings are one line and both body sentences are one line, so swapping
   * them moves nothing. If either string is ever edited, re-check that — a
   * second line here reintroduces exactly the jump this branch removes.
   */
  if (state === 'loading' || state === 'success') {
    const arrived = state === 'success'
    // The CTA waits for the LAST beat rather than for `state`, so it lands
    // after the check has finished writing instead of on top of it.
    const ctaIn = phase === 'done'
    const heading = arrived ? "You're all set" : 'Confirming your payment'
    return (
      <Shell showLogo={false}>
        {/* `spacing={0}` + explicit margins rather than `spacing={6}`: a Stack
            gap applies to the CTA wrapper even when that wrapper has collapsed
            to zero height, which would leave a 24px hole below the text while
            the button is still hidden. Moving that gap INSIDE the collapsing
            region makes it collapse with the button. */}
        <Stack spacing={0} alignItems="center" width="100%">
          <SignupSuccessMark phase={phase} />
          <Stack spacing={3} alignItems="center" sx={{ mt: 6 }}>
            {/* `key` is load-bearing: it forces a remount when the heading
                changes, which is what re-runs the per-word settle. Without it
                the spans keep their finished animation state and the new
                heading appears whole. */}
            <AssemblingHeading key={heading} text={heading} reduceMotion={prefersReducedMotion} />
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
              sx={{ lineHeight: 1.5 }}
            >
              {arrived
                ? 'Payment received and your company is ready.'
                : "This takes a few seconds. Please don't close this page."}
            </Typography>
          </Stack>

          {/* THE CTA, revealed on the last beat.

              `grid-template-rows` 0fr -> 1fr is the height animation: a grid
              row sized in `fr` is animatable where `height: auto` is not, so
              the container grows to exactly the button's height without anyone
              measuring it. The inner box MUST carry `overflow: hidden` and
              `minHeight: 0` — without them the child refuses to shrink below
              its content and the row never collapses.

              The button is rendered by the STATE, not by the animation: under
              reduced motion `phase` is `done` immediately, and if the
              transition never runs the row still resolves to 1fr. There is no
              path where a paid customer is left without this button. */}
          <Box
            sx={{
              width: '100%',
              display: 'grid',
              gridTemplateRows: ctaIn ? '1fr' : '0fr',
              transition: prefersReducedMotion
                ? 'none'
                : 'grid-template-rows 340ms cubic-bezier(0.22, 1, 0.36, 1)',
            }}
          >
            <Box sx={{ overflow: 'hidden', minHeight: 0 }}>
              <Box
                sx={{
                  pt: 6,
                  opacity: ctaIn ? 1 : 0,
                  // Rises the last few px as it fades, so it reads as arriving
                  // rather than as being switched on.
                  transform: ctaIn ? 'translateY(0)' : 'translateY(8px)',
                  transition: prefersReducedMotion
                    ? 'none'
                    : 'opacity 240ms ease-out 60ms, transform 340ms cubic-bezier(0.22, 1, 0.36, 1) 60ms',
                }}
              >
                <AuthPrimaryButton fullWidth onClick={goHome}>
                  Start connecting
                </AuthPrimaryButton>
              </Box>
            </Box>
          </Box>
        </Stack>
      </Shell>
    )
  }

  if (state === 'pending') {
    return (
      <Shell showLogo={false}>
        <Stack spacing={6} alignItems="center" width="100%">
          <SignupSuccessMark phase="waiting" />
          <Stack spacing={3} alignItems="center">
            <Typography variant="h1" textAlign="center">
              Payment received
            </Typography>
          </Stack>
          {/* Deliberately NOT an error. The charge succeeded; only the activation
              is lagging, and the webhook completes it with or without this tab. */}
          <Alert
            severity="info"
            description="Your payment went through. We're still finishing your account setup, which can take a moment. You can safely close this page. We'll email you when it's ready."
          />
          <Stack direction="row" spacing={3} width="100%">
            <Button variant="secondary" onClick={handleRetry}>
              Check again
            </Button>
            <Button fullWidth onClick={() => navigate(ROUTES.SIGN_IN)}>
              Go to sign in
            </Button>
          </Stack>
        </Stack>
      </Shell>
    )
  }

  if (state === 'failed') {
    return (
      <Shell showLogo={false}>
        <Stack spacing={6} alignItems="center" width="100%">
          {/* The same mark, in the failure tone — red pulse, red iris, an X.
              A failure screen with no mark at all read as a different product
              from the one the visitor was just looking at. */}
          <SignupSuccessMark phase="check" tone="error" />
          <Stack spacing={3} alignItems="center">
            <Typography variant="h1" textAlign="center">
              That payment didn't go through
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
              sx={{ lineHeight: 1.5 }}
            >
              You haven't been charged. Nothing you entered is lost. Your details are saved, so
              picking up where you left off only needs a payment method.
            </Typography>
          </Stack>
          <Alert severity="error" description={errorMessage ?? ''} />
          {/* Back to the stepper rather than a retry button here.

              The PaymentIntent this page holds is bound to the plan and
              interval it was minted with, so re-confirming it is not a clean
              retry — and this page has no way to mint a new one, because the
              call that mints it also carries the company details, which live in
              the stepper. Returning there is what re-mints correctly. */}
          <AuthPrimaryButton fullWidth onClick={() => navigate(ROUTES.SIGN_UP_SELF_SERVE)}>
            Try payment again
          </AuthPrimaryButton>
        </Stack>
      </Shell>
    )
  }

  return (
    <Shell showLogo={false}>
      <Stack spacing={6} alignItems="center" width="100%">
        <SignupSuccessMark phase="check" tone="error" />
        <Stack spacing={3} alignItems="center">
          <Typography variant="h1" textAlign="center">
            We couldn't confirm your payment
          </Typography>
          {/* Say this plainly. Someone who has just paid and hit an error screen
              needs to know the charge is not lost before anything else. */}
          <Typography
            variant="body2"
            color="text.secondary"
            textAlign="center"
            sx={{ lineHeight: 1.5 }}
          >
            If you were charged, your payment is safe and your account will still be set up.
            Contact support and we'll finish it for you.
          </Typography>
        </Stack>
        {/* The reference is what makes "contact support" actionable — it is the
            one thing support needs to find the charge, and the visitor cannot be
            expected to dig it out of the URL. Shown only when we have it. */}
        <Stack spacing={2} width="100%" alignItems="center">
          <Alert severity="error" description={errorMessage ?? ''} />
          {reference && (
            <Typography variant="body3" color="text.secondary" textAlign="center">
              Reference: {reference}
            </Typography>
          )}
        </Stack>
        {/* ⚠️ THE ORDER HERE IS A MONEY DECISION, not a layout one.

            This state means VERIFY failed — we do not know whether the charge
            landed. "Try again" re-runs verification only; it moves no money and
            is the correct first action, so it is the PRIMARY.

            "Back to signup" replaced "Go to sign in" on request. It carries a
            real hazard and the copy above has to keep carrying it: if the
            original payment DID succeed and verification was merely slow, a
            visitor who goes back and pays again is charged twice. Signing in
            could not do that, which is why it was the original destination.
            The mitigations are that retry is primary and that the body copy
            says the payment is safe — neither is a guarantee. A server-side
            guard (refuse to mint a second intent while one is unresolved for
            the same email) is the real fix and does not exist yet. */}
        <Stack direction="row" spacing={3} width="100%">
          <Button variant="secondary" onClick={() => navigate(ROUTES.SIGN_UP_SELF_SERVE)}>
            Back to signup
          </Button>
          <Button fullWidth onClick={handleRetry}>
            Try again
          </Button>
        </Stack>
      </Stack>
    </Shell>
  )
}
